/**
 * The right-hand preview of whatever is highlighted in the command bar.
 *
 * A record's detail is fetched only once the highlight has rested for a
 * moment, so arrowing down a list of eight costs one request, not eight. The
 * row's own data shows at once while the rest loads.
 */
import type { ReactNode } from 'react';
import { Badge, Group, Kbd, Loader, Stack, Text, UnstyledButton } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useLeadDetail } from '../../api/queries';
import type { components } from '../../api/schema';
import { TOKENS, formatDateTime, formatGBP, formatNumber } from '../../theme';
import { MonoLabel } from '../../ui';
import { relativeTime } from '../../ui/format';
import { useVocab } from '../../vocab';
import type { Command } from '../commands';
import { StatusPill, leadName } from './rows';

export type LeadRow = components['schemas']['LeadSummary'];

export type QuickAction = {
  id: 'open' | 'leads' | 'copy-email' | 'copy-phone' | 'save';
  label: string;
  keys: string[];
};

/** The height the results and the preview share. */
export const LIST_HEIGHT = 440;

/** How long the highlight must rest before a record's detail is fetched. */
export const PREVIEW_DELAY = 180;

export function PreviewPane({
  lead,
  command,
  actions,
  onAction,
}: {
  lead: LeadRow | null;
  command: Command | null;
  actions: QuickAction[];
  onAction: (a: QuickAction) => void;
}) {
  let body: ReactNode = <Text size="sm" c="dimmed">Highlight a result to see it here.</Text>;
  if (lead) body = <LeadPreview lead={lead} />;
  else if (command) body = <CommandPreview command={command} />;
  return (
    <div
      data-testid="cmdk-preview"
      style={{
        width: 340, flexShrink: 0, borderLeft: `1px solid ${TOKENS.border}`, padding: '14px 16px',
        overflowY: 'auto', maxHeight: LIST_HEIGHT, display: 'flex', flexDirection: 'column', gap: 10,
      }}
    >
      <div style={{ flex: '1 1 auto' }}>{body}</div>
      {actions.length > 0 && (
        <Stack gap={2}>
          {actions.map((a) => (
            <UnstyledButton
              key={a.id}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onAction(a)}
              style={{ borderRadius: 6, padding: '3px 8px' }}
              className="cmdk-quick-action"
            >
              <Group justify="space-between" wrap="nowrap" gap="xs">
                <Text size="sm">{a.label}</Text>
                <Group gap={3} wrap="nowrap">
                  {a.keys.map((k) => <Kbd key={k} size="xs">{k}</Kbd>)}
                </Group>
              </Group>
            </UnstyledButton>
          ))}
        </Stack>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  const empty = children === '' || children === null || children === undefined;
  return (
    <div style={{ minWidth: 0 }}>
      <MonoLabel style={{ fontSize: 10 }}>{label}</MonoLabel>
      <div style={{ fontSize: 13, color: empty ? TOKENS.textMuted : TOKENS.text, overflowWrap: 'anywhere' }}>
        {empty ? '–' : children}
      </div>
    </div>
  );
}

function LeadPreview({ lead }: { lead: LeadRow }) {
  const v = useVocab();
  const [id] = useDebouncedValue(lead.id, PREVIEW_DELAY);
  const detail = useLeadDetail(id === lead.id ? id : null);
  const d = detail.data && detail.data.id === lead.id ? detail.data : null;
  const routing = d?.distribution.routing ?? [];
  return (
    <Stack gap={12}>
      <div>
        <Text fw={700} size="md" style={{ overflowWrap: 'anywhere' }}>{leadName(lead)}</Text>
        <Group gap={6} mt={4}>
          <StatusPill lead={lead} />
          <Text size="xs" c="dimmed">{relativeTime(lead.created_at)}</Text>
        </Group>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 12px' }}>
        <div style={{ gridColumn: '1 / -1' }}><Row label="Email">{lead.email}</Row></div>
        <Row label="Phone">{lead.phone1}</Row>
        <Row label="Value">{formatGBP(lead.revenue)}</Row>
        <Row label={v.One('campaign')}>{lead.campaign_name}</Row>
        <Row label={v.One('supplier')}>{lead.supplier_name}</Row>
        <Row label={v.One('buyer')}>{lead.buyer_name}</Row>
        <Row label="Created">{formatDateTime(lead.created_at)}</Row>
      </div>
      {detail.isFetching && !d && (
        <Group gap={6}><Loader size={12} /><Text size="xs" c="dimmed">Loading detail</Text></Group>
      )}
      {routing.length > 0 && (
        <div style={{ border: `1px solid ${TOKENS.border}`, borderRadius: 8, padding: '8px 10px' }}>
          <Text size="xs" fw={600} mb={4}>How it was routed</Text>
          <Stack gap={4}>
            {routing.slice(0, 6).map((line, i) => <Text key={i} size="xs">{line}</Text>)}
          </Stack>
        </div>
      )}
    </Stack>
  );
}

function CommandPreview({ command }: { command: Command }) {
  const v = useVocab();
  const m = command.meta;
  const Icon = command.icon;
  const head = (
    <Group gap={8} wrap="nowrap">
      <Icon size={18} stroke={1.6} />
      <Text fw={700} size="md" style={{ overflowWrap: 'anywhere' }}>{command.label}</Text>
    </Group>
  );
  if (!m) {
    return (
      <Stack gap={6}>
        {head}
        {command.description && <Text size="sm" c="dimmed">{command.description}</Text>}
      </Stack>
    );
  }
  switch (m.kind) {
    case 'campaign':
      return (
        <Stack gap={10}>
          {head}
          <Group gap={6}>
            <Badge size="sm" variant="light" color={m.active ? 'chartGreen' : 'gray'}>{m.active ? 'Active' : 'Paused'}</Badge>
            {m.isReference && <Badge size="sm" variant="outline" color="gray">Reference list</Badge>}
          </Group>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 12px' }}>
            <Row label="Today">{m.today === null ? '' : formatNumber(m.today)}</Row>
            <Row label={v.Many('routeGroup')}>{formatNumber(m.groups)}</Row>
            <Row label={v.Many('destination')}>{formatNumber(m.destinations)}</Row>
          </div>
          {m.groups === 0 && !m.isReference && (
            <Text size="xs" c="dimmed">{`Nothing is routed yet: add ${v.a('routeGroup')} to start delivering.`}</Text>
          )}
        </Stack>
      );
    case 'template':
      return (
        <Stack gap={10}>
          {head}
          <Row label="Subject">{m.subject}</Row>
          <Row label="Last edited">{relativeTime(m.updatedAt)}</Row>
        </Stack>
      );
    case 'segment':
      return (
        <Stack gap={10}>
          {head}
          <Row label={v.Many('lead')}>{m.count === null ? '' : formatNumber(m.count)}</Row>
        </Stack>
      );
    case 'entity':
      return (
        <Stack gap={10}>
          {head}
          <Badge size="sm" variant="light" color={m.active ? 'chartGreen' : 'gray'}>{m.active ? 'Active' : 'Inactive'}</Badge>
        </Stack>
      );
  }
}
